/**
 * ChallengeNode - Devil's Advocate challenge nodes
 *
 * Red-styled nodes that highlight risks, counter-arguments, or logical fallacies
 */

import React, { memo, useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Swords, Brain, Zap, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useMapStore } from '../../stores/mapStore';

// Icon mapping for different challenge types
const challengeIcons = {
  risk: AlertTriangle,
  'counter-argument': Swords,
  fallacy: Brain,
  'missing-consideration': Zap,
  contradiction: AlertTriangle,
};

const ChallengeNode = memo(({ data, id, selected }) => {
  const { content, title, metadata } = data;
  const challengeType = metadata?.challengeType || 'risk';
  const Icon = challengeIcons[challengeType] || AlertTriangle;
  const [isExpanded, setIsExpanded] = useState(false);

  const { deleteNode } = useMapStore();

  const shouldTruncate = content?.length > 150;

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0, rotate: -5 }}
      animate={{ scale: 1, opacity: 1, rotate: 0 }}
      layout
      className={`group transition-all duration-300 ${isExpanded ? 'w-80' : 'w-52'}`}
    >
      {/* Connection handles */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-red-500 !border-2 !border-red-900"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-red-500 !border-2 !border-red-900"
      />

      {/* Challenge card */}
      <div
        className={`relative rounded-xl overflow-hidden bg-red-950/40 border border-red-500/50 transition-all duration-200 ${
          selected ? 'ring-2 ring-red-400 shadow-lg shadow-red-500/20' : ''
        }`}
      >
        {/* Pulsing border effect */}
        <div className="absolute inset-0 rounded-xl border border-red-500/30 animate-pulse" />

        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2 bg-red-900/30 border-b border-red-500/30">
          <Icon size={14} className="text-red-400" />
          <span className="flex-1 text-sm font-medium text-red-300 truncate">
            {title || "Devil's Advocate"}
          </span>
          <div
            className="nodrag nopan nowheel"
            style={{ pointerEvents: 'all' }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => deleteNode(id)}
              className="p-1 rounded hover:bg-red-800/50 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X size={12} className="text-red-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-3">
          <AnimatePresence mode="wait">
            <motion.p
              key={isExpanded ? 'expanded' : 'collapsed'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-sm text-red-200/90 leading-relaxed"
            >
              {isExpanded ? content : content?.substring(0, 150)}
              {!isExpanded && shouldTruncate && '...'}
            </motion.p>
          </AnimatePresence>

          {/* Expand/Collapse button */}
          {shouldTruncate && (
            <div
              className="nodrag nopan nowheel"
              style={{ pointerEvents: 'all' }}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-1 mt-2 text-xs text-red-400 hover:text-red-300 transition-colors cursor-pointer select-none"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp size={12} />
                    <span>Daha az göster</span>
                  </>
                ) : (
                  <>
                    <ChevronDown size={12} />
                    <span>Devamını oku</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Challenge type badge */}
        <div className="px-3 pb-2">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-900/50 rounded text-[10px] text-red-400 capitalize">
            {challengeType.replace('-', ' ')}
          </span>
        </div>
      </div>

      {/* Devil's advocate label */}
      <div className="absolute -top-5 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2 py-0.5 bg-red-500/20 rounded text-[10px] text-red-300 whitespace-nowrap">
        <Swords size={10} />
        <span>Challenge</span>
      </div>
    </motion.div>
  );
});

ChallengeNode.displayName = 'ChallengeNode';

export default ChallengeNode;
